import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { WorkspaceBySlug } from '../services/workspaceService';
import * as workspaceService from '../services/workspaceService';
import { Building2, ArrowRight, Home, Sun, Moon, Monitor } from 'lucide-react';
import { getContrastColor } from '../utils/colorUtils';

const OrganizationHome: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { themeMode, setThemeMode, effectiveTheme } = useTheme();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [workspace, setWorkspace] = useState<WorkspaceBySlug | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getThemeIcon = () => {
    const icons = { light: Sun, dark: Moon, system: Monitor };
    const Icon = icons[themeMode];
    return <Icon className="w-5 h-5" />;
  };

  useEffect(() => {
    const loadWorkspace = async () => {
      if (!slug) {
        setError('No workspace slug provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await workspaceService.getWorkspaceBySlug(slug);
        setWorkspace(data);
      } catch (err: any) {
        console.error('Failed to load workspace:', err);
        setError(err.message || 'Workspace not found');
        setWorkspace(null);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspace();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-secondary-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-900 dark:border-white mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-secondary-900">
        <div className="text-center max-w-md">
          <Building2 className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">Workspace Not Found</h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">
            {error || 'The workspace you are looking for does not exist.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            <Home className="h-5 w-5" />
            <span>Go to Home</span>
          </Link>
        </div>
      </div>
    );
  }

  // Resolve theme colors based on effective theme (light or dark)
  const isDark = effectiveTheme === 'dark';
  const bgColor = isDark ? workspace.theme_dark_bg : workspace.theme_light_bg;
  const headingColor = isDark ? workspace.theme_dark_heading : workspace.theme_light_heading;
  const subtextColor = isDark
    ? workspace.theme_dark_heading + 'aa'  // 67% opacity
    : workspace.theme_light_heading + '99'; // 60% opacity
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const surfaceColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const primaryColor = workspace.workspace_primary_color;
  const btnTextColor = getContrastColor(primaryColor);
  const isOwner = user && workspace.id === user.id;
  const fontFamily = workspace.workspace_font_family
    ? `'${workspace.workspace_font_family}', sans-serif`
    : undefined;

  return (
    <div className="min-h-screen transition-colors" style={{ backgroundColor: bgColor, fontFamily }}>

      {/* Header */}
      <header style={{ borderBottom: `1px solid ${borderColor}` }}>
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {workspace.workspace_logo_url && !workspace.workspace_logo_url.includes('localhost:') ? (
                <>
                  <img
                    src={workspace.workspace_logo_url}
                    alt={`${workspace.workspace_name} logo`}
                    className="w-14 h-14 object-cover rounded-full"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl hidden"
                    style={{ backgroundColor: primaryColor, color: btnTextColor }}
                  >
                    {workspace.workspace_name.charAt(0).toUpperCase()}
                  </div>
                </>
              ) : (
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl"
                  style={{ backgroundColor: primaryColor, color: btnTextColor }}
                >
                  {workspace.workspace_name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold" style={{ color: headingColor }}>
                  {workspace.workspace_name}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <div className="relative">
                <button
                  onClick={() => setShowThemeMenu(!showThemeMenu)}
                  className="p-2.5 rounded-lg transition-all"
                  style={{
                    backgroundColor: surfaceColor,
                    color: headingColor,
                    border: `1px solid ${borderColor}`,
                  }}
                  aria-label="Toggle theme"
                >
                  {getThemeIcon()}
                </button>
                {showThemeMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowThemeMenu(false)} />
                    <div
                      className="absolute top-full right-0 mt-2 w-48 rounded-lg shadow-xl overflow-hidden z-50"
                      style={{
                        backgroundColor: isDark ? 'rgba(30,30,40,0.98)' : '#ffffff',
                        border: `1px solid ${borderColor}`,
                      }}
                    >
                      {(['light', 'dark', 'system'] as const).map((mode) => {
                        const icons = { light: Sun, dark: Moon, system: Monitor };
                        const labels = { light: 'Light', dark: 'Dark', system: 'Auto (System)' };
                        const Icon = icons[mode];
                        const isActive = themeMode === mode;
                        return (
                          <button
                            key={mode}
                            onClick={() => { setThemeMode(mode); setShowThemeMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                            style={{
                              color: headingColor,
                              backgroundColor: isActive ? surfaceColor : 'transparent',
                            }}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{labels[mode]}</span>
                            {isActive && (
                              <div
                                className="ml-auto w-2 h-2 rounded-full"
                                style={{ backgroundColor: headingColor }}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm"
                style={{
                  backgroundColor: surfaceColor,
                  color: headingColor,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <Home className="h-4 w-4" />
                <span>Home</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div
          className="rounded-lg p-12 text-center"
          style={{ backgroundColor: surfaceColor, border: `1px solid ${borderColor}` }}
        >
          <div
            className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
          >
            <Building2 className="h-12 w-12" />
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ color: headingColor }}>
            Welcome to {workspace.workspace_name}
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: subtextColor }}>
            {workspace.name}&apos;s workspace on Wisetree AI.
            {!user && ' Sign in to view projects and collaborate.'}
          </p>

          {isOwner ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg transition-opacity hover:opacity-90 text-lg"
              style={{ backgroundColor: primaryColor, color: btnTextColor }}
            >
              <span>Go to Dashboard</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          ) : user ? (
            <div
              className="max-w-md mx-auto rounded-lg p-6"
              style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}` }}
            >
              <p className="mb-4" style={{ color: headingColor }}>
                You are signed in as <strong>{user.email}</strong>.
              </p>
              <p className="text-sm" style={{ color: subtextColor }}>
                This is not your workspace. If you need access, please contact the workspace owner.
              </p>
            </div>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg transition-opacity hover:opacity-90 text-lg"
              style={{ backgroundColor: primaryColor, color: btnTextColor }}
            >
              <span>Sign In</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm" style={{ color: subtextColor }}>
            Powered by{' '}
            <Link to="/" className="font-medium" style={{ color: primaryColor }}>
              Wisetree AI
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default OrganizationHome;
